import { useCallback, useEffect, useRef, useState } from 'react';

export type NameCheckStatus = null | 'checking' | 'available' | 'unavailable';

export interface CheckAvailabilityResult {
  exists: boolean;
  orderId?: string | null;
}

export interface UseOrderNameAvailabilityOptions {
  checkAvailability: (name: string) => Promise<CheckAvailabilityResult>;
  debounceMs?: number;
  /**
   * When provided, an input matching this value short-circuits to a cleared
   * state without calling the API. Used by OrderEdit so that the operator
   * isn't told their *current* order name is "unavailable".
   */
  excludeName?: string | null;
}

export interface UseOrderNameAvailabilityResult {
  status: NameCheckStatus;
  existingOrderId: string | null;
  onNameChange: (nextName: string) => void;
  reset: () => void;
}

/**
 * Debounced order-name uniqueness probe.
 *
 * Status transitions:
 *   typing  → (debounceMs)  → 'checking' → 'available' | 'unavailable'
 *
 * `'checking'` is set INSIDE the debounce timer (not on every keystroke) so
 * that intermediate keystrokes don't trigger re-renders that interrupt the
 * input — this matches the behaviour reached after iteration in OrderNew.jsx.
 *
 * Errors:
 *   - 404 from the backend means "no such name" → treated as `available`.
 *   - Any other error logs to console and clears status to `null`. We never
 *     swallow silently: the user shouldn't be allowed to submit thinking
 *     the name is fine when the probe actually failed.
 */
export function useOrderNameAvailability({
  checkAvailability,
  debounceMs = 500,
  excludeName,
}: UseOrderNameAvailabilityOptions): UseOrderNameAvailabilityResult {
  const [status, setStatus] = useState<NameCheckStatus>(null);
  const [existingOrderId, setExistingOrderId] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkAvailabilityRef = useRef(checkAvailability);
  const excludeNameRef = useRef(excludeName);

  // Keep refs in sync so the closure inside setTimeout always uses the latest
  // values — without re-binding `onNameChange` (which would otherwise reset
  // any pending debounce on every parent re-render).
  checkAvailabilityRef.current = checkAvailability;
  excludeNameRef.current = excludeName;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    setStatus(null);
    setExistingOrderId(null);
  }, [clearTimer]);

  const onNameChange = useCallback(
    (nextName: string) => {
      clearTimer();

      const exclude = excludeNameRef.current;
      if (exclude != null && nextName === exclude) {
        setStatus(null);
        setExistingOrderId(null);
        return;
      }

      if (!nextName || nextName.trim() === '') {
        setStatus(null);
        setExistingOrderId(null);
        return;
      }

      timerRef.current = setTimeout(async () => {
        setStatus('checking');
        try {
          const result = await checkAvailabilityRef.current(nextName);
          if (result.exists) {
            setStatus('unavailable');
            setExistingOrderId(result.orderId ?? null);
          } else {
            setStatus('available');
            setExistingOrderId(null);
          }
        } catch (error) {
          const message = (error as { message?: string } | null)?.message;
          if (message && message.includes('404')) {
            setStatus('available');
            setExistingOrderId(null);
          } else {
            console.error('Failed to check order name:', error);
            setStatus(null);
          }
        }
      }, debounceMs);
    },
    [clearTimer, debounceMs],
  );

  useEffect(() => clearTimer, [clearTimer]);

  return { status, existingOrderId, onNameChange, reset };
}
